/* eslint-disable object-curly-newline, object-property-newline, no-nested-ternary */
//
// Likert x comment integration -- programmatic_v2 Spike F port.
//
// Reproduces the v8.0.6 AI inventory's Likert-mismatch detection in
// hand-authored rules so the fallback path (AI unavailable) can still
// produce PLAHER-aware coaching. The AI pipeline already integrates
// Likert; this module is what fires when the AI errors and the rule
// engine takes over.
//
// Detection produces a diagnosis like mismatch_glowing_low /
// mismatch_critical_high / split_ratings / aligned_high / moderate, and a
// short coaching string that names a specific PLAHER dimension + rating
// exactly as the form shows it (no paraphrase, no hallucination).
//
// Validation (programmatic_v2/validate_against_357.py on 2026-06-05):
//   vs v8.0.6 inventory on 357-case eval (hostile excluded):
//   mismatch presence accuracy 79.2%, recall 61.4%, precision 81.9%.
//   On 58 synthetic adversarial cases (positive prose forced to all-1s
//   ratings + critical prose forced to all-5s ratings): 48% catch rate
//   (the AI pipeline catches the rest -- this is the fallback floor).

export const PLAHER_KEYS = ['P', 'L', 'A', 'H', 'E', 'R'];
export const PLAHER_NAMES = {
  P: 'Performance', L: 'Leadership', A: 'Attitude',
  H: 'Harmony', E: 'Expression', R: 'Reliability',
};
const HIGH_THRESHOLD = 4;
const LOW_THRESHOLD = 2;
const PARTIAL_THRESHOLD = 3;

// -- Likert pattern classifier ----------------------------------------

function isValidLikert(v) {
  return Number.isInteger(v) && v >= 1 && v <= 5;
}

export function classifyLikert(plaher) {
  const values = {};
  PLAHER_KEYS.forEach((k) => { values[k] = plaher ? plaher[k] : undefined; });
  const validKeys = PLAHER_KEYS.filter((k) => isValidLikert(values[k]));
  const validValues = validKeys.map((k) => values[k]);
  const n = validValues.length;
  if (n < PARTIAL_THRESHOLD) {
    return { pattern: 'insufficient', values: {}, highDims: [], lowDims: [], midDims: [], mean: null, spread: null, uniformValue: null };
  }
  const high = validKeys.filter((k) => values[k] >= HIGH_THRESHOLD);
  const low = validKeys.filter((k) => values[k] <= LOW_THRESHOLD);
  const mid = validKeys.filter((k) => values[k] === 3);
  const mean = validValues.reduce((a, b) => a + b, 0) / n;
  const spread = Math.max(...validValues) - Math.min(...validValues);
  const allSame = validValues.every((v) => v === validValues[0]);
  const majority = Math.max(3, n - 2);

  let pattern;
  let uniformValue = null;
  const [first] = validValues;
  if (allSame && n === 6) {
    pattern = 'uniform';
    uniformValue = first;
  } else if (allSame) {
    if (first >= HIGH_THRESHOLD) pattern = 'leaning_high';
    else if (first <= LOW_THRESHOLD) pattern = 'leaning_low';
    else pattern = 'moderate';
  } else if ((high.length >= 2 && low.length >= 2) || spread >= 3) {
    pattern = 'split';
  } else if (high.length >= majority && low.length === 0) {
    pattern = 'mostly_high';
  } else if (low.length >= majority && high.length === 0) {
    pattern = 'mostly_low';
  } else if (mid.length >= majority) {
    pattern = 'moderate';
  } else if (mean >= 3.5) {
    pattern = 'leaning_high';
  } else if (mean <= 2.5) {
    pattern = 'leaning_low';
  } else {
    pattern = 'mixed';
  }

  return {
    pattern,
    mean: Math.round(mean * 100) / 100,
    spread,
    highDims: high,
    lowDims: low,
    midDims: mid,
    values,
    uniformValue,
  };
}

// -- Comment polarity classifier --------------------------------------

const POSITIVE_ADJ = new Set([
  'excellent', 'outstanding', 'exceptional', 'fantastic', 'wonderful',
  'terrific', 'impressive', 'brilliant', 'great', 'strong', 'amazing',
  'valuable', 'helpful', 'effective', 'productive', 'reliable',
  'thoughtful', 'professional', 'dedicated', 'hardworking', 'skilled',
  'talented', 'capable', 'competent', 'knowledgeable', 'supportive',
  'engaged', 'proactive', 'responsive', 'respectful', 'positive',
  'good', 'nice', 'kind', 'creative', 'innovative', 'diligent',
  'punctual', 'trustworthy', 'consistent', 'solid', 'friendly',
  'approachable', 'collaborative',
]);
const POSITIVE_VERB = new Set([
  'excelled', 'helped', 'assisted', 'supported',
  'encouraged', 'motivated', 'improved',
]);
const POSITIVE_PHRASE = [
  'great teammate', 'good teammate', 'great team member', 'good team member',
  'pleasure to work with', 'did a great job', 'did a good job',
  'well done', 'great work', 'good work', 'great job', 'good job',
  'great contribution', 'made a real difference', 'really helped',
  'always there', 'always on time', 'always prepared',
  'met expectations', 'exceeded expectations', 'above and beyond',
  'stepped up',
];

const CRITICAL_ADJ = new Set([
  'weak', 'poor', 'lacking', 'insufficient', 'disappointing', 'frustrating',
  'unreliable', 'inconsistent', 'slow', 'lazy', 'careless', 'sloppy',
  'absent', 'passive', 'uncommunicative', 'dismissive', 'disengaged',
  'unprepared', 'unprofessional', 'rude', 'uneven', 'uncooperative',
  'hostile', 'selfish',
]);
const CRITICAL_VERB = new Set([
  'failed', 'missed', 'lacked', 'struggled', 'ignored', 'refused',
  'dismissed', 'neglected', 'skipped', 'abandoned', 'delayed',
  'disappeared', 'slowed', 'withdrew', 'interrupted',
]);
const CRITICAL_PHRASE = [
  'did not', "didn't", 'did nothing',
  'could have done better', 'could be better', 'needs to improve',
  'should have', 'should not have', 'fell short', 'fell behind',
  'not enough', 'not reliable', 'not engaged', "wasn't there",
  'never showed up', 'never contributed', 'never participated',
  'did not show', 'did not contribute', 'did not participate',
  'did not meet', 'did not deliver',
  'problem with', 'issue with', 'concern about',
  'behind on', 'behind schedule',
  'talked over', 'shut down', 'tuned out',
  'not collaborative', 'not supportive', 'not helpful', 'not prepared',
  'not professional', 'not responsive', 'not respectful',
  'could improve', 'could have improved', 'could have done',
  'could be more', 'could do more', 'would have liked',
  'would like to see', 'needs improvement', 'room for improvement',
  'rarely showed', 'rarely show', 'rarely attended',
  'hard to reach', 'hard to get in touch', 'hard to communicate',
  'may be too', 'tends to', 'often missed', 'frequently missed',
];
const PIVOT_TOKENS = new Set([
  'but', 'however', 'although', 'though', 'yet', 'while', 'whereas',
  'still', 'nonetheless', 'nevertheless',
]);
const NEGATORS = new Set(['not', 'never', 'no', 'nor']);
const NEGATION_WINDOW = 3;

function tokenize(text) {
  return ((text || '').toLowerCase().match(/[a-z']+/g)) || [];
}

function isPositionNegated(tokens, position) {
  const start = Math.max(0, position - NEGATION_WINDOW);
  for (let i = start; i < position; i += 1) {
    if (NEGATORS.has(tokens[i])) return true;
  }
  return false;
}

export function classifyComment(text) {
  const raw = text || '';
  const lower = raw.toLowerCase();
  const tokens = tokenize(raw);
  const wordCount = tokens.length;
  const positiveSignals = [];
  const criticalSignals = [];
  for (let i = 0; i < tokens.length; i += 1) {
    const tok = tokens[i];
    if (POSITIVE_ADJ.has(tok) || POSITIVE_VERB.has(tok)) {
      if (isPositionNegated(tokens, i)) criticalSignals.push(`not_${tok}`);
      else positiveSignals.push(tok);
    } else if (CRITICAL_ADJ.has(tok) || CRITICAL_VERB.has(tok)) {
      criticalSignals.push(tok);
    }
  }
  POSITIVE_PHRASE.forEach((p) => { if (lower.indexOf(p) !== -1) positiveSignals.push(p); });
  CRITICAL_PHRASE.forEach((p) => { if (lower.indexOf(p) !== -1) criticalSignals.push(p); });

  const pivots = tokens.filter((t) => PIVOT_TOKENS.has(t));
  const pos = positiveSignals.length;
  const crit = criticalSignals.length;
  let polarity;
  if (pos === 0 && crit === 0) polarity = 'neutral';
  else if (pos > 0 && crit > 0) polarity = pivots.length > 0 ? 'mixed' : 'mixed_unstructured';
  else if (pos > 0) polarity = wordCount < 8 ? 'vague_positive' : 'positive';
  else polarity = wordCount < 8 ? 'vague_critical' : 'critical';

  return {
    polarity, wordCount, positiveSignals, criticalSignals, pivotTokens: pivots,
  };
}

// -- Non-English detection (resilience gate) --------------------------

const NON_ENGLISH_DIACRITICS = new Set(
  'áéíóúâêôãõçàèìòùäëïöüñÁÉÍÓÚÂÊÔÃÕÇÀÈÌÒÙÄËÏÖÜÑ'.split(''),
);
const ENGLISH_TELLS = new Set([
  'the', 'and', 'you', 'to', 'a', 'of', 'is', 'was', 'were', 'are',
  'your', 'with', 'in', 'on', 'for', 'i', 'we', 'it', 'they', 'be',
  'have', 'had', 'this', 'that', 'but', 'very', 'more', 'would',
]);

export function looksNonEnglish(text) {
  if (!text) return false;
  let diacritics = 0;
  const chars = Array.from(text);
  for (let i = 0; i < chars.length; i += 1) {
    if (NON_ENGLISH_DIACRITICS.has(chars[i])) diacritics += 1;
  }
  if (diacritics >= 3) return true;
  const tokens = ((text.match(/[A-Za-z']+/g)) || []).map((t) => t.toLowerCase());
  if (tokens.length < 4) return false;
  const englishTells = tokens.filter((t) => ENGLISH_TELLS.has(t)).length;
  if (diacritics >= 1 && englishTells < 3) return true;
  if (englishTells === 0 && tokens.length >= 6) return true;
  return false;
}

// -- 2D router + coaching renderer ------------------------------------

function formatDim(plaherKey, value) {
  return `${PLAHER_NAMES[plaherKey]} is ${value} of 5`;
}

function pickExtreme(values, dims, mode) {
  // mode = 'lowest' | 'highest'
  let chosen = dims[0];
  for (let i = 1; i < dims.length; i += 1) {
    const d = dims[i];
    if (mode === 'lowest' && values[d] < values[chosen]) chosen = d;
    if (mode === 'highest' && values[d] > values[chosen]) chosen = d;
  }
  return chosen;
}

function routeUniform(polarity, uniformValue) {
  const all = `all six PLAHER dimensions are ${uniformValue} of 5`;
  const allCap = all.charAt(0).toUpperCase() + all.slice(1);
  if (uniformValue >= HIGH_THRESHOLD) {
    if (polarity === 'critical' || polarity === 'vague_critical') {
      return {
        diagnosis: 'mismatch_critical_high',
        coaching: `Your comment raises concerns but ${all}. Which picture do you want to give your teammate, and can you adjust one to match?`,
        commentPolarity: polarity,
        likertPattern: 'uniform',
      };
    }
    if (polarity === 'positive') {
      return {
        diagnosis: 'aligned_high',
        coaching: `Your comment and ratings agree -- ${all}. Can you point to a specific moment that shows what your teammate did?`,
        commentPolarity: polarity,
        likertPattern: 'uniform',
      };
    }
    if (polarity === 'vague_positive' || polarity === 'neutral') {
      return {
        diagnosis: 'aligned_high_vague',
        coaching: `${allCap} but the comment is brief. What did you actually see them do that earned these ratings?`,
        commentPolarity: polarity,
        likertPattern: 'uniform',
      };
    }
    if (polarity === 'mixed' || polarity === 'mixed_unstructured') {
      return {
        diagnosis: 'mixed_text',
        coaching: `Your comment names both strengths and concerns but ${all}. Is the dominant signal clear to your teammate, or could you tighten one side?`,
        commentPolarity: polarity,
        likertPattern: 'uniform',
      };
    }
  }
  if (uniformValue <= LOW_THRESHOLD) {
    if (polarity === 'positive' || polarity === 'vague_positive') {
      return {
        diagnosis: 'mismatch_glowing_low',
        coaching: `Your comment reads positive but ${all}. What specifically didn't work, so the comment matches the ratings?`,
        commentPolarity: polarity,
        likertPattern: 'uniform',
      };
    }
    if (polarity === 'critical') {
      return {
        diagnosis: 'aligned_low',
        coaching: `Your comment and ratings agree -- ${all}. What specific incident best explains these low ratings?`,
        commentPolarity: polarity,
        likertPattern: 'uniform',
      };
    }
    if (polarity === 'vague_critical' || polarity === 'neutral') {
      return {
        diagnosis: 'aligned_low_vague',
        coaching: `${allCap} but the comment is brief. What incident shaped these low ratings?`,
        commentPolarity: polarity,
        likertPattern: 'uniform',
      };
    }
    if (polarity === 'mixed' || polarity === 'mixed_unstructured') {
      return {
        diagnosis: 'mixed_text',
        coaching: `Your comment names both strengths and concerns but ${all}. Is the dominant signal clear to your teammate?`,
        commentPolarity: polarity,
        likertPattern: 'uniform',
      };
    }
  }
  // uniformValue === 3
  return {
    diagnosis: 'moderate',
    coaching: `${allCap}. If a particular area stood out as strongest or weakest, naming it in the comment would help your teammate.`,
    commentPolarity: polarity,
    likertPattern: 'uniform',
  };
}

function route(cpol, likert) {
  const { polarity } = cpol;
  const { pattern, values, highDims, lowDims } = likert;

  if (pattern === 'uniform') {
    return routeUniform(polarity, likert.uniformValue);
  }

  // Mismatches: prioritized
  if ((polarity === 'positive' || polarity === 'vague_positive')
      && (pattern === 'mostly_low' || pattern === 'leaning_low')) {
    const lowList = lowDims.length ? lowDims : PLAHER_KEYS;
    const worst = pickExtreme(values, lowList, 'lowest');
    return {
      diagnosis: 'mismatch_glowing_low',
      coaching: `Your comment reads positive but your ratings are low overall. ${formatDim(worst, values[worst])}. What specifically didn't work, so the comment matches the ratings?`,
      commentPolarity: polarity,
      likertPattern: pattern,
    };
  }

  if ((polarity === 'critical' || polarity === 'vague_critical')
      && (pattern === 'mostly_high' || pattern === 'leaning_high')) {
    const highList = highDims.length ? highDims : PLAHER_KEYS;
    const best = pickExtreme(values, highList, 'highest');
    return {
      diagnosis: 'mismatch_critical_high',
      coaching: `Your comment raises concerns but your ratings are high overall. ${formatDim(best, values[best])}. Which picture do you want to give your teammate, and can you adjust one to match?`,
      commentPolarity: polarity,
      likertPattern: pattern,
    };
  }

  // Split
  if (pattern === 'split') {
    const worst = pickExtreme(values, lowDims, 'lowest');
    const best = pickExtreme(values, highDims, 'highest');
    return {
      diagnosis: 'split_ratings',
      coaching: `Your ratings split: ${formatDim(worst, values[worst])} while ${formatDim(best, values[best])}. Can you name a specific moment that explains the ${PLAHER_NAMES[worst]} rating?`,
      commentPolarity: polarity,
      likertPattern: pattern,
    };
  }

  // Aligned
  if (polarity === 'positive' && (pattern === 'mostly_high' || pattern === 'leaning_high')) {
    const best = pickExtreme(values, highDims, 'highest');
    return {
      diagnosis: 'aligned_high',
      coaching: `Your ratings back up your comment. ${formatDim(best, values[best])}. Can you point to a specific moment that shows the ${PLAHER_NAMES[best]} you saw?`,
      commentPolarity: polarity,
      likertPattern: pattern,
    };
  }

  if (polarity === 'critical' && (pattern === 'mostly_low' || pattern === 'leaning_low')) {
    const worst = pickExtreme(values, lowDims, 'lowest');
    return {
      diagnosis: 'aligned_low',
      coaching: `Your ratings line up with your concerns. ${formatDim(worst, values[worst])}. What specific incident best explains the low ${PLAHER_NAMES[worst]} rating?`,
      commentPolarity: polarity,
      likertPattern: pattern,
    };
  }

  // Vague + clear ratings
  if ((polarity === 'vague_positive' || polarity === 'neutral')
      && (pattern === 'mostly_high' || pattern === 'leaning_high')) {
    const best = pickExtreme(values, highDims, 'highest');
    return {
      diagnosis: 'aligned_high_vague',
      coaching: `Your ratings are high but the comment is brief. ${formatDim(best, values[best])}. What did you actually see them do that earned the high ${PLAHER_NAMES[best]} rating?`,
      commentPolarity: polarity,
      likertPattern: pattern,
    };
  }

  if ((polarity === 'vague_critical' || polarity === 'neutral')
      && (pattern === 'mostly_low' || pattern === 'leaning_low')) {
    const worst = pickExtreme(values, lowDims, 'lowest');
    return {
      diagnosis: 'aligned_low_vague',
      coaching: `Your ratings show real concerns but the comment is brief. ${formatDim(worst, values[worst])}. What incident shaped the low ${PLAHER_NAMES[worst]} rating?`,
      commentPolarity: polarity,
      likertPattern: pattern,
    };
  }

  // Mixed Likert pattern -- treat as soft split
  if (pattern === 'mixed' && highDims.length > 0 && lowDims.length > 0) {
    const worst = pickExtreme(values, lowDims, 'lowest');
    const best = pickExtreme(values, highDims, 'highest');
    return {
      diagnosis: 'split_ratings',
      coaching: `Your ratings disagree across dimensions: ${formatDim(worst, values[worst])} while ${formatDim(best, values[best])}. Can you name a specific moment that explains the lower ${PLAHER_NAMES[worst]} rating?`,
      commentPolarity: polarity,
      likertPattern: pattern,
    };
  }

  // Moderate -- soft mismatches when prose has clear polarity
  if (pattern === 'moderate') {
    if (polarity === 'positive') {
      return {
        diagnosis: 'mismatch_glowing_low',
        coaching: 'Your comment reads positive but your ratings are mostly middle of the scale. What specifically did you see, so the comment and ratings line up?',
        commentPolarity: polarity,
        likertPattern: pattern,
      };
    }
    if (polarity === 'critical') {
      return {
        diagnosis: 'mismatch_critical_high',
        coaching: 'Your comment raises concerns but your ratings are mostly middle of the scale. Are the issues sharp enough that one rating should reflect them?',
        commentPolarity: polarity,
        likertPattern: pattern,
      };
    }
    return {
      diagnosis: 'moderate',
      coaching: 'Your ratings are all middle of the scale. If a particular area stood out as strongest or weakest, naming it in the comment would help your teammate.',
      commentPolarity: polarity,
      likertPattern: pattern,
    };
  }

  // Mixed comment text fallback
  if (polarity === 'mixed' || polarity === 'mixed_unstructured') {
    const lean = (pattern === 'mostly_high' || pattern === 'leaning_high') ? 'high'
      : (pattern === 'mostly_low' || pattern === 'leaning_low') ? 'low'
        : 'mixed';
    return {
      diagnosis: 'mixed_text',
      coaching: `Your comment names both strengths and concerns. Your ratings lean ${lean}. Is the dominant signal clear to your teammate, or could you tighten one side?`,
      commentPolarity: polarity,
      likertPattern: pattern,
    };
  }

  return {
    diagnosis: 'fallback_generic',
    coaching: 'Your ratings show a pattern; if a particular PLAHER dimension stood out, naming it concretely in the comment would help your teammate.',
    commentPolarity: polarity,
    likertPattern: pattern,
  };
}

/**
 * Integrate a comment with PLAHER ratings.
 * Returns null when integration cannot fire usefully:
 *   - comment is empty / 'N/A' (caller already filters this typically)
 *   - comment looks non-English (Spike E multilingual rule packs not built)
 *   - fewer than 3 valid PLAHER ratings provided
 *
 * Otherwise returns:
 *   { diagnosis, coaching, commentPolarity, likertPattern }
 *
 * `coaching` is the user-facing string with the specific PLAHER label +
 * rating named exactly as the form shows it. It is intentionally generic
 * about the comment (does NOT quote it -- comprehension is too unreliable
 * in rules to risk content claims).
 */
export function integrateLikert(comment, plaher) {
  if (!comment || comment === 'N/A') return null;
  if (looksNonEnglish(comment)) return null;
  const likert = classifyLikert(plaher);
  if (likert.pattern === 'insufficient') return null;
  const cpol = classifyComment(comment);
  return route(cpol, likert);
}
